import { GridRenderCellParams } from '@mui/x-data-grid';
import { AgentPortalFeatureCode } from '@wallet-manager/pfh-pmp-node-def-types';

import { IdefaultFilter } from '../';
import APIs from '../../../../api';
import { ApiResult } from '../../../../api/types';
import { OpView } from '../../../../assets/icons';
import Table from '../../../../common/filterTable/components/Table';
import FilterContainer from '../../../../common/filterTable/layout/FilterContainer';
import FilterTableLayoutContainer from '../../../../common/filterTable/layout/FilterTableLayoutContainer';
import TableContainer from '../../../../common/filterTable/layout/TableContainer';
import useNewFilterTablePage from '../../../../common/page/hooks/useNewFilterTablePage';
import { Currency } from '../../../../common/types/Currency';
import { ExportBtn, OpIconButton } from '../../../../components/Button';
import { useDatePicker } from '../../../../components/DatePicker';
import { DivideLine } from '../../../../components/Layout';
import useTranslation from '../../../../hooks/useTranslation';
import { getTabTranslatePrefix } from '../helper';
import FilterSection from './components/FilterSection';
import useGetListParams from './helpers/useGetListParams';
import useTableColumns from './helpers/useTableColumns';
import useTableRows from './helpers/useTableRows';
import { FilterFields, FilterFieldsKeys } from './types/FilterFields';
import { GetListRes } from './types/GetListRes';
import { TableColumnKey } from './types/Table';

const initFields = {
  [FilterFieldsKeys.DistributorAgentId]: '',
  [FilterFieldsKeys.Currency]: {} as Currency,
};

const translatePrefix = getTabTranslatePrefix('balanceTab');
const getTableDataApiFunc =
  APIs.distributorAgentManagement.distributorAgentWallet.balanceTab.getAll;
const exportTableApiFunc =
  APIs.distributorAgentManagement.distributorAgentWallet.balanceTab.getExport;

const exportBtnPermission =
  AgentPortalFeatureCode.DistributorAgentManagement.DistributorAgentWallet.BalanceTab.Export;

const BalanceTab = ({
  redirectToHistoryTab,
}: {
  redirectToHistoryTab: (defaultFields: IdefaultFilter) => void;
}) => {
  const { getListParams } = useGetListParams();
  const { t } = useTranslation(translatePrefix);

  const DateObj = {
    CreationTime: useDatePicker(),
    LastModifiedTime: useDatePicker(),
  };

  const pageConfig = {
    initFields,
    DateObj,
    translatePrefix,
    getListParams,
    getTableDataApiFunc,
    exportTableApiFunc,
  };

  const {
    page,
    onPageChange,
    fields,
    setFields,
    list,
    onFilterSearch,
    onFilterReset,
    onExport,
    getIsHavePermission,
    Tabs,
  } = useNewFilterTablePage<FilterFields, ApiResult<GetListRes>>(pageConfig);

  const { rows, totalCounts, convertToTableRows } = useTableRows({ apiList: list });
  const columns = useTableColumns({ renderOperationCell });

  const isShowExportBtn = getIsHavePermission(exportBtnPermission);

  const handleExport = async () => {
    await onExport('Distributor Agent Wallet Balance', totalCounts, convertToTableRows);
  };

  function renderOperationCell(params: GridRenderCellParams) {
    const currency = params.row[TableColumnKey.Currency] as string;
    const distributorAgentId = params.row[TableColumnKey.DistributorAgentId] as string;
    const handleClick = () => {
      redirectToHistoryTab({ currency, distributorAgentId });
    };

    return <OpIconButton title={t('history_button')} svgUrl={OpView} onClick={handleClick} />;
  }

  return (
    <>
      <FilterTableLayoutContainer>
        <FilterContainer>
          <FilterSection
            fields={fields}
            setFields={setFields}
            onSearch={onFilterSearch}
            onReset={onFilterReset}
            DateObj={DateObj}
          />
        </FilterContainer>

        <DivideLine />

        <TableContainer>
          <ExportBtn onExport={handleExport} isShow={isShowExportBtn} />
          <Tabs>
            <Table
              columns={columns}
              rows={rows}
              rowCount={totalCounts}
              page={page}
              onPageChange={onPageChange}
            />
          </Tabs>
        </TableContainer>
      </FilterTableLayoutContainer>
    </>
  );
};

export default BalanceTab;
